import { Paged } from 'api/common/types/Page';
import AnnouncementsService from 'api/announcements/AnnouncementsService';
import { AnnouncementDto } from 'api/announcements/models/AnnouncementDto';
import { AnnouncementSearchCriteria } from 'api/announcements/models/AnnouncementSearchCriteria';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import Box from 'common/components/box/Box';
import Dropdown from 'common/components/dropdown/Dropdown';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import DropdownItem from 'common/components/popover/PopoverItem';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { CompactPageLayout } from 'common/components/compactPageLayout';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Logger from 'common/services/Logger';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, DATE_TIME_FORMAT_DEFAUT, LOGGER_LOG_TYPE } from 'Config';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEllipsisH } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { Reducers } from 'store/types';
import moment from 'moment';
import { AnnouncementStyle } from 'api/announcements/models/AnnouncementStyle';
import { resolveAnnouncementColors } from 'common/components/announcements/announcementStyleColors';
import { resolveTranslation } from 'common/components/announcements/resolveTranslation';
import { applyDateToken } from 'common/components/announcements/applyDateToken';
import { sanitizeInline } from 'common/components/announcements/sanitizeInline';
import styles from './AdminAnnouncementsScreen.module.scss';

type Props = {
};

const AdminAnnouncementsScreen: React.FC<Props> = ({ }: Props) => {
    const { t, i18n } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();

    const [criteria, setCriteria] = useState<AnnouncementSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'desc',
        orderColumn: 'a.starts_at'
    });
    const [announcementsPage, setAnnouncementsPage] = useState<Paged<AnnouncementDto>>();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COMPANIES_WRITE']);

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<AnnouncementDto | null>(null);

    const getData = async () => {
        try {
            const page = await AnnouncementsService.getList(criteria);
            setAnnouncementsPage(page);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get announcements list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    useEffect(() => {
        getData();
    }, [criteria]);

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    };

    const navigateTo = (type?: string, id?: string) => {
        if (id) {
            history.push(`/avisos/${type}/${id}`);
        } else {
            history.push(`/avisos/${type}`);
        }
    };

    const onRowClick = (event: any, id?: string) => {
        if (id) {
            if (event.ctrlKey) {
                window.open(`/avisos/details/${id}`, '_blank');
                return;
            }
            navigateTo('details', id);
        }
    };

    const showRemoveItemDialog = (item: AnnouncementDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }
        try {
            await AnnouncementsService.remove(itemToRemove);
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
            setCriteria({ ...criteria, page: 1 });
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete announcement`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const renderDateCell = (value: string | Date) => <>{value ? moment(value).format(DATE_TIME_FORMAT_DEFAUT) : '-'}</>;

    const renderStyleCell = (row: AnnouncementDto) => {
        const colors = resolveAnnouncementColors(row.style, row.textColor, row.backgroundColor);
        return <span className={styles.styleBadge} style={{ color: colors.textColor, backgroundColor: colors.backgroundColor }}>
            {t(`announcements.styles.${(row.style || AnnouncementStyle.WARNING).toLowerCase()}` as any)}
        </span>;
    };

    const renderTableActionCell = (row: AnnouncementDto) => {
        return <Dropdown
            options={
                <>
                    <DropdownItem url={`avisos/details/${row.id}`}>
                        {t('common.details')}
                    </DropdownItem>
                    {hasWritePolicy && <DropdownItem url={`avisos/edit/${row.id}`}>
                        {t('common.edit')}
                    </DropdownItem>}
                    {hasWritePolicy && <DropdownItem url={`avisos/copy/${row.id}`}>
                        {t('common.copy')}
                    </DropdownItem>}
                    {hasWritePolicy && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                        {t('common.remove')}
                    </DropdownItem>}
                </>
            }
        >
            <div>
                <FaEllipsisH />
            </div>
        </Dropdown>;
    };

    const tableColumns: ListingTableColumn<AnnouncementDto>[] = [
        {
            field: 'startsAt',
            name: t('announcements.list.starts_at'),
            onSearch: onTableFilter,
            searchField: 'a.starts_at',
            renderCell: row => renderDateCell(row.startsAt)
        },
        {
            field: 'endsAt',
            name: t('announcements.list.ends_at'),
            onSearch: onTableFilter,
            searchField: 'a.ends_at',
            renderCell: row => renderDateCell(row.endsAt)
        },
        {
            name: t('announcements.list.title'),
            renderCell: row => <>{resolveTranslation(row.translations, i18n.language).title}</>
        },
        {
            name: t('announcements.list.message'),
            renderCell: row => <span dangerouslySetInnerHTML={{ __html: sanitizeInline(applyDateToken(resolveTranslation(row.translations, i18n.language).message, row.eventAt)) }} />
        },
        {
            name: t('announcements.list.style'),
            width: '140px',
            preventClick: true,
            renderCell: renderStyleCell
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        },
    ];

    return (
        <ScreenTitle title={t('announcements.title')}>
            <CompactPageLayout title={t('announcements.title') as string} headerButtons={<>
                {hasWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => navigateTo('new')} />}
            </>}>
                <div className={styles.contentWrapper}>
                <Box>
                    <ListingTable
                        columns={tableColumns}
                        rows={announcementsPage?.items || []}
                        onRowClick={(row, _, event) => onRowClick(event, row.id)}
                        allowHover={true}
                        initialSearch={{ colField: 'a.starts_at', isOrderAsc: false }}
                    />
                    <PaginationWithInfo
                        itemName={t('announcements.title')}
                        currentPage={announcementsPage?.currentPage ?? 1}
                        pageItems={announcementsPage?.items.length || 0}
                        totalItems={announcementsPage?.totalItems || 0}
                        onChange={page => setCriteria({ ...criteria, page: page })}
                    />
                </Box>
                </div>
                <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record_with_ident', { name: resolveTranslation(itemToRemove?.translations, i18n.language).title })} />
            </CompactPageLayout>
        </ScreenTitle>
    );
};

export default AdminAnnouncementsScreen;
